import React, { useState, useEffect } from 'react';
import { FaClock, FaSave, FaCheck, FaExclamationTriangle, FaList, FaToggleOn, FaToggleOff, FaSearch } from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';

const ReminderSettings = () => {
    const [activeTab, setActiveTab] = useState('settings'); // settings | items
    const [settings, setSettings] = useState({
        contracts: { enabled: true, initialDelay: 3, interval: 3 },
        quotes: { enabled: true, initialDelay: 3, interval: 5 },
        invoices: { enabled: true, upcomingDays: 3, overdueInterval: 3 }
    });
    const [items, setItems] = useState({ contracts: [], quotes: [], invoices: [] });
    const [loading, setLoading] = useState(true);
    const [loadingItems, setLoadingItems] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadSettings();
    }, []);

    useEffect(() => {
        if (activeTab === 'items') {
            loadActiveItems();
        }
    }, [activeTab]);

    const loadSettings = async () => {
        try {
            const [res1, res2] = await Promise.all([
                fetch(API_ENDPOINTS.ADMIN_REMINDER_SETTINGS_CONTRACTS),
                fetch(API_ENDPOINTS.ADMIN_REMINDER_SETTINGS_ITSERVICES)
            ]);

            // Even if one fails, try to use the other? For now assume strict success.
            if (res1.ok && res2.ok) {
                const data1 = await res1.json();
                const data2 = await res2.json();

                setSettings({
                    contracts: data1.contracts || settings.contracts,
                    quotes: data2.quotes || settings.quotes,
                    invoices: data2.invoices || settings.invoices
                });
            }
        } catch (error) {
            console.error('Error loading settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadActiveItems = async () => {
        setLoadingItems(true);
        try {
            const [res1, res2] = await Promise.all([
                fetch(`${API_ENDPOINTS.ADMIN_REMINDER_SETTINGS_CONTRACTS}?action=active-items`),
                fetch(`${API_ENDPOINTS.ADMIN_REMINDER_SETTINGS_ITSERVICES}?action=active-items`)
            ]);

            if (res1.ok && res2.ok) {
                const data1 = await res1.json();
                const data2 = await res2.json();

                setItems({
                    contracts: data1.contracts || [],
                    quotes: data2.quotes || [],
                    invoices: data2.invoices || []
                });
            }
        } catch (error) {
            console.error('Error loading active items:', error);
        } finally {
            setLoadingItems(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);
        try {
            const [res1, res2] = await Promise.all([
                fetch(API_ENDPOINTS.ADMIN_REMINDER_SETTINGS_CONTRACTS, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json',
 'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}` },
                    body: JSON.stringify(settings)
                }),
                fetch(API_ENDPOINTS.ADMIN_REMINDER_SETTINGS_ITSERVICES, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json',
 'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}` },
                    body: JSON.stringify(settings)
                })
            ]);

            if (res1.ok && res2.ok) {
                setMessage({ type: 'success', text: 'Settings saved successfully' });
            } else {
                throw new Error('Failed to save settings');
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            setMessage({ type: 'error', text: 'Failed to save settings' });
        } finally {
            setSaving(false);
        }
    };

    const handleToggleItem = async (type, id, currentSkip) => {
        // Optimistic update
        const newSkip = !currentSkip;
        const section = type === 'contract' ? 'contracts' : type === 'quote' ? 'quotes' : 'invoices';

        setItems(prev => ({
            ...prev,
            [section]: prev[section].map(item =>
                item.id === id ? { ...item, skipReminders: newSkip } : item
            )
        }));

        try {
            const url = type === 'contract'
                ? API_ENDPOINTS.ADMIN_REMINDER_SETTINGS_CONTRACTS
                : API_ENDPOINTS.ADMIN_REMINDER_SETTINGS_ITSERVICES;

            await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json',
 'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}` },
                body: JSON.stringify({
                    action: 'toggle-item',
                    type,
                    id,
                    skip: newSkip
                })
            });
        } catch (error) {
            console.error('Error toggling item:', error);
            // Revert on error
            setItems(prev => ({
                ...prev,
                [section]: prev[section].map(item =>
                    item.id === id ? { ...item, skipReminders: currentSkip } : item
                )
            }));
        }
    };

    const handleChange = (section, field, value) => {
        setSettings(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: field === 'enabled' ? value : parseInt(value)
            }
        }));
    };

    const filterItems = (list) => {
        if (!searchTerm) return list;
        const lower = searchTerm.toLowerCase();
        return list.filter(item =>
            item.number.toLowerCase().includes(lower) ||
            item.name.toLowerCase().includes(lower)
        );
    };

    if (loading) {
        return <div className="p-8 text-center text-[#bdae93]">Loading settings...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-[#ebdbb2]">Automated Reminders</h2>
                    <p className="text-[#bdae93]">Configure schedule and frequency for automated emails</p>
                </div>
                <div className="flex space-x-2 bg-[#282828] p-1 rounded-lg">
                    <button
                        onClick={() => setActiveTab('settings')}
                        className={`px-4 py-2 rounded-md font-medium transition-colors ${activeTab === 'settings'
                            ? 'bg-[#fabd2f] text-[#1d2021]'
                            : 'text-[#bdae93] hover:text-[#ebdbb2]'
                            }`}
                    >
                        <FaClock className="inline mr-2" />
                        Settings
                    </button>
                    <button
                        onClick={() => setActiveTab('items')}
                        className={`px-4 py-2 rounded-md font-medium transition-colors ${activeTab === 'items'
                            ? 'bg-[#fabd2f] text-[#1d2021]'
                            : 'text-[#bdae93] hover:text-[#ebdbb2]'
                            }`}
                    >
                        <FaList className="inline mr-2" />
                        Manage Items
                    </button>
                </div>
            </div>

            {message && (
                <div className={`p-4 rounded-lg flex items-center ${message.type === 'success' ? 'bg-[#dcfce7] text-[#166534]' : 'bg-[#fee2e2] text-[#991b1b]'
                    }`}>
                    {message.type === 'success' ? <FaCheck className="mr-2" /> : <FaExclamationTriangle className="mr-2" />}
                    {message.text}
                </div>
            )}

            {activeTab === 'settings' ? (
                /* --- SETTINGS TAB --- */
                <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Contract Section */}
                        <div className="bg-[#282828] p-6 rounded-lg border border-[#3c3836]">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-[#fabd2f]">Contracts</h3>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={settings.contracts.enabled}
                                        onChange={(e) => handleChange('contracts', 'enabled', e.target.checked)}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-[#504945] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#fabd2f]"></div>
                                </label>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-[#bdae93] mb-1">First Reminder Delay (Days)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={settings.contracts.initialDelay}
                                        onChange={(e) => handleChange('contracts', 'initialDelay', e.target.value)}
                                        className="w-full bg-[#3c3836] border border-[#504945] rounded-md px-3 py-2 text-[#ebdbb2] focus:outline-none focus:border-[#fabd2f]"
                                    />
                                    <p className="text-xs text-[#928374] mt-1">Days after correct sending date</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[#bdae93] mb-1">Reminder Interval (Days)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={settings.contracts.interval}
                                        onChange={(e) => handleChange('contracts', 'interval', e.target.value)}
                                        className="w-full bg-[#3c3836] border border-[#504945] rounded-md px-3 py-2 text-[#ebdbb2] focus:outline-none focus:border-[#fabd2f]"
                                    />
                                    <p className="text-xs text-[#928374] mt-1">Days between subsequent reminders</p>
                                </div>
                            </div>
                        </div>

                        {/* Quote Section */}
                        <div className="bg-[#282828] p-6 rounded-lg border border-[#3c3836]">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-[#83a598]">Quotes</h3>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={settings.quotes.enabled}
                                        onChange={(e) => handleChange('quotes', 'enabled', e.target.checked)}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-[#504945] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#83a598]"></div>
                                </label>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-[#bdae93] mb-1">First Reminder Delay (Days)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={settings.quotes.initialDelay}
                                        onChange={(e) => handleChange('quotes', 'initialDelay', e.target.value)}
                                        className="w-full bg-[#3c3836] border border-[#504945] rounded-md px-3 py-2 text-[#ebdbb2] focus:outline-none focus:border-[#83a598]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[#bdae93] mb-1">Reminder Interval (Days)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={settings.quotes.interval}
                                        onChange={(e) => handleChange('quotes', 'interval', e.target.value)}
                                        className="w-full bg-[#3c3836] border border-[#504945] rounded-md px-3 py-2 text-[#ebdbb2] focus:outline-none focus:border-[#83a598]"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Invoice Section */}
                        <div className="bg-[#282828] p-6 rounded-lg border border-[#3c3836]">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-[#b8bb26]">Invoices</h3>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={settings.invoices.enabled}
                                        onChange={(e) => handleChange('invoices', 'enabled', e.target.checked)}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-[#504945] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#b8bb26]"></div>
                                </label>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-[#bdae93] mb-1">Upcoming Reminder (Days Before)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={settings.invoices.upcomingDays}
                                        onChange={(e) => handleChange('invoices', 'upcomingDays', e.target.value)}
                                        className="w-full bg-[#3c3836] border border-[#504945] rounded-md px-3 py-2 text-[#ebdbb2] focus:outline-none focus:border-[#b8bb26]"
                                    />
                                    <p className="text-xs text-[#928374] mt-1">Days before due date to remind</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[#bdae93] mb-1">Overdue Reminder Interval (Days)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={settings.invoices.overdueInterval}
                                        onChange={(e) => handleChange('invoices', 'overdueInterval', e.target.value)}
                                        className="w-full bg-[#3c3836] border border-[#504945] rounded-md px-3 py-2 text-[#ebdbb2] focus:outline-none focus:border-[#b8bb26]"
                                    />
                                    <p className="text-xs text-[#928374] mt-1">Frequency of overdue notices</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t border-[#3c3836]">
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-[#fabd2f] text-[#1d2021] px-6 py-2 rounded-lg font-semibold hover:bg-[#d79921] transition-colors flex items-center disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : (
                                <>
                                    <FaSave className="mr-2" />
                                    Save Settings
                                </>
                            )}
                        </button>
                    </div>
                </>
            ) : (
                /* --- ACTIVE ITEMS TAB --- */
                <div className="space-y-6">
                    <div className="flex items-center space-x-4 bg-[#282828] p-4 rounded-lg border border-[#3c3836]">
                        <FaSearch className="text-[#bdae93]" />
                        <input
                            type="text"
                            placeholder="Search by number or customer name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="bg-transparent text-[#ebdbb2] placeholder-[#7c6f64] focus:outline-none w-full"
                        />
                    </div>

                    {loadingItems ? (
                        <div className="text-center p-8 text-[#bdae93]">Loading active items...</div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                            {/* Contracts List */}
                            <div className="bg-[#282828] rounded-lg border border-[#3c3836] overflow-hidden">
                                <div className="p-4 bg-[#3c3836] border-b border-[#504945]">
                                    <h3 className="font-semibold text-[#fabd2f]">Active Contracts</h3>
                                </div>
                                <div className="divide-y divide-[#3c3836] max-h-[500px] overflow-y-auto">
                                    {filterItems(items.contracts).map(item => (
                                        <div key={item.id} className="p-4 flex items-center justify-between hover:bg-[#32302f]">
                                            <div>
                                                <div className="font-medium text-[#ebdbb2]">{item.number}</div>
                                                <div className="text-sm text-[#bdae93]">{item.name}</div>
                                                <div className="text-xs text-[#7c6f64]">{new Date(item.date).toLocaleDateString()}</div>
                                            </div>
                                            <button
                                                onClick={() => handleToggleItem('contract', item.id, item.skipReminders)}
                                                className={`text-2xl transition-colors ${item.skipReminders ? 'text-[#7c6f64]' : 'text-[#fabd2f]'}`}
                                                title={item.skipReminders ? "Reminders Disabled" : "Reminders Enabled"}
                                            >
                                                {item.skipReminders ? <FaToggleOff /> : <FaToggleOn />}
                                            </button>
                                        </div>
                                    ))}
                                    {filterItems(items.contracts).length === 0 && <div className="p-4 text-center text-[#7c6f64]">No items found</div>}
                                </div>
                            </div>

                            {/* Quotes List */}
                            <div className="bg-[#282828] rounded-lg border border-[#3c3836] overflow-hidden">
                                <div className="p-4 bg-[#3c3836] border-b border-[#504945]">
                                    <h3 className="font-semibold text-[#83a598]">Active Quotes</h3>
                                </div>
                                <div className="divide-y divide-[#3c3836] max-h-[500px] overflow-y-auto">
                                    {filterItems(items.quotes).map(item => (
                                        <div key={item.id} className="p-4 flex items-center justify-between hover:bg-[#32302f]">
                                            <div>
                                                <div className="font-medium text-[#ebdbb2]">{item.number}</div>
                                                <div className="text-sm text-[#bdae93]">{item.name}</div>
                                                <div className="text-xs text-[#7c6f64]">${item.amount} • {new Date(item.date).toLocaleDateString()}</div>
                                            </div>
                                            <button
                                                onClick={() => handleToggleItem('quote', item.id, item.skipReminders)}
                                                className={`text-2xl transition-colors ${item.skipReminders ? 'text-[#7c6f64]' : 'text-[#83a598]'}`}
                                                title={item.skipReminders ? "Reminders Disabled" : "Reminders Enabled"}
                                            >
                                                {item.skipReminders ? <FaToggleOff /> : <FaToggleOn />}
                                            </button>
                                        </div>
                                    ))}
                                    {filterItems(items.quotes).length === 0 && <div className="p-4 text-center text-[#7c6f64]">No items found</div>}
                                </div>
                            </div>

                            {/* Invoices List */}
                            <div className="bg-[#282828] rounded-lg border border-[#3c3836] overflow-hidden">
                                <div className="p-4 bg-[#3c3836] border-b border-[#504945]">
                                    <h3 className="font-semibold text-[#b8bb26]">Pending Invoices</h3>
                                </div>
                                <div className="divide-y divide-[#3c3836] max-h-[500px] overflow-y-auto">
                                    {filterItems(items.invoices).map(item => (
                                        <div key={item.id} className="p-4 flex items-center justify-between hover:bg-[#32302f]">
                                            <div>
                                                <div className="font-medium text-[#ebdbb2]">{item.number}</div>
                                                <div className="text-sm text-[#bdae93]">{item.name}</div>
                                                <div className="text-xs text-[#7c6f64]">${item.amount} • {new Date(item.dueDate).toLocaleDateString()}</div>
                                            </div>
                                            <button
                                                onClick={() => handleToggleItem('invoice', item.id, item.skipReminders)}
                                                className={`text-2xl transition-colors ${item.skipReminders ? 'text-[#7c6f64]' : 'text-[#b8bb26]'}`}
                                                title={item.skipReminders ? "Reminders Disabled" : "Reminders Enabled"}
                                            >
                                                {item.skipReminders ? <FaToggleOff /> : <FaToggleOn />}
                                            </button>
                                        </div>
                                    ))}
                                    {filterItems(items.invoices).length === 0 && <div className="p-4 text-center text-[#7c6f64]">No items found</div>}
                                </div>
                            </div>

                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ReminderSettings;
